import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { UserService } from "src/app/core/auth";
import { GlobalRequestPrefix } from "src/app/core/constants";
import { PaginationV2 } from "src/app/core/pagination-v2";
import { RequestV2Service } from "src/app/core/request-v2.service";
import {
  ColumnSort,
  firstExistentValueFrom,
  mapNullable,
  SortOrder,
} from "src/utils";
import { normalizeColumnSort } from "src/utils/set-sort-on-resource";
import { CarrierListQueryModel } from "./carrier-list-query.model";
import { CarrierSelectedListQueryModel } from "./carrier-selected-list-query.model";
import { CarrierType } from "./carrier-type.model";
import {
  CarrierCreate,
  CarrierUpdate,
  CarrierUpdateArgs,
} from "./carrier-update.model";
import {
  Carrier,
  CarrierDetails,
  CarrierReference,
  deserializeSimpleCarrier,
  SimpleCarrier,
} from "./carrier.model";

@Injectable({ providedIn: "root" })
export class CarriersService {
  public constructor(
    private readonly request: RequestV2Service,
    private readonly user: UserService,
  ) {}

  public readonly typesChanges = this.request
    .get({
      prefix: GlobalRequestPrefix,
      path: "/carrierTypes",
    })
    .pipe(
      map((result) => result && CarrierType.deserializeList(result)),
      shareReplay(1),
    );

  public getListChanges({
    query,
    isActive,
    carrierKey,
    carrierTypeName,
    nameQuery,
    sort = { key: "name", order: SortOrder.Ascending },
    maximum,
    pagination = new PaginationV2(),
  }: {
    query?: string;
    isActive?: boolean;
    carrierKey?: string;
    carrierTypeName?: string;
    nameQuery?: string;
    sort?: ColumnSort | "name";
    maximum?: number;
    pagination?: PaginationV2;
  } = {}): Observable<readonly Carrier[] | null> {
    const paginationToUse = maximum
      ? new PaginationV2(
          { pageSize: maximum, currentPage: 1 },
          { preserveCustomValues: true },
        )
      : pagination;
    return this.request
      .getPaged({
        prefix: GlobalRequestPrefix,
        path: "/Carriers/All",
        data: new CarrierListQueryModel({
          searchQuery: query,
          isActive,
          carrierKey,
          carrierTypeName,
          name: nameQuery,
          sort: normalizeColumnSort(sort),
          pagination: paginationToUse,
        }),
        pagination: paginationToUse,
        shouldPreserveInitialPagination: !!maximum,
      })
      .pipe(
        mapNullable((response) => Carrier.deserializeList(response.items)),
        shareReplay(1),
      );
  }

  public getSelectedList({
    nameQuery,
    siteId,
    sort = { key: "name", order: SortOrder.Ascending },
  }: {
    nameQuery?: string;
    siteId?: number;
    sort?: ColumnSort | "name";
  } = {}): Observable<readonly SimpleCarrier[] | null> {
    const pagination = new PaginationV2({ pageSize: 20, currentPage: 1 });
    return this.request
      .getPaged({
        prefix: GlobalRequestPrefix,
        path: "/Carriers/SelectedList",
        data: new CarrierSelectedListQueryModel({
          searchQuery: nameQuery,
          siteId,
          sort: normalizeColumnSort(sort),
          pagination,
        }),
        pagination,
      })
      .pipe(
        mapNullable((response) =>
          response.items.map((carrier) => deserializeSimpleCarrier(carrier)),
        ),
        shareReplay(1),
      );
  }

  public async getUserCarrier(): Promise<Carrier | null> {
    if (!this.user.isCarrier()) {
      return null;
    }

    const carriers = await firstExistentValueFrom(
      this.getListChanges({
        carrierKey: this.user.details.carrierKey ?? undefined,
      }),
    );

    // This is a workaround for the fact that the API does not return the only one carrier with the given key because they filter out with "contain" not "equals".
    // We should remove this once the API is fixed to return the carrier with the given key only.
    const foundCarrier = carriers.find(
      (carrier) => carrier.key === this.user.details.carrierKey,
    );

    return foundCarrier ?? null;
  }

  public get(id: number): Promise<CarrierDetails | null> {
    return firstExistentValueFrom(
      this.request
        .get({
          prefix: GlobalRequestPrefix,
          path: "/Carriers/{id}",
          pathParams: { id },
        })
        .pipe(mapNullable((response) => CarrierDetails.deserialize(response))),
    );
  }

  public async update(
    update: CarrierUpdateArgs,
  ): Promise<CarrierDetails | null> {
    return update.base
      ? await this.patch(update.base.id, new CarrierUpdate(update))
      : await this.post(new CarrierCreate(update));
  }

  private patch(
    id: CarrierReference["id"],
    data: CarrierUpdate,
  ): Promise<CarrierDetails | null> {
    return firstExistentValueFrom(
      this.request
        .patch({
          prefix: GlobalRequestPrefix,
          path: "/Carriers/{id}",
          pathParams: { id },
          data,
        })
        .pipe(mapNullable((response) => CarrierDetails.deserialize(response))),
    );
  }

  private post(carrier: CarrierCreate): Promise<CarrierDetails | null> {
    return firstExistentValueFrom(
      this.request
        .post({
          prefix: GlobalRequestPrefix,
          path: "/Carriers",
          data: carrier,
        })
        .pipe(mapNullable((response) => CarrierDetails.deserialize(response))),
    );
  }
}
